import invert from 'lodash/invert';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import type {
  ArdoqId,
  FutureStateClaim,
  FutureStateClaimGroup,
  FutureStateRenderClaim,
  FutureStateRenderRequest,
} from '@ardoq/api-types';
import { untargetedComponentClaimWithParentEnforced } from './untargetedComponentClaimWithParentEnforced';

/** An alias from this or an earlier batch, or the id of a persisted claim, group or entity. */
export type DraftAlias = string;

export type DraftKind = 'create' | 'update' | 'delete';

export type DraftKindAndVersion = {
  draftKind: DraftKind;
  draftVersion: number;
};

export type Drafted<Entity> = Entity & DraftKindAndVersion;

export type MaybeDrafted<Entity> = Entity & Partial<DraftKindAndVersion>;

export type FutureStateDrafts = {
  futureStateId: ArdoqId;
  latestDraftVersion: number;
  accepting: boolean;
  draftIdByAlias: Record<DraftAlias, ArdoqId>;
  claims: Record<ArdoqId, Drafted<FutureStateClaim>>;
  claimGroups: Record<ArdoqId, Drafted<FutureStateClaimGroup>>;
};

export type FutureStateDraftClaimsState = Record<ArdoqId, FutureStateDrafts>;

export type PersistedClaims = {
  claims: FutureStateClaim[];
  claimGroups: FutureStateClaimGroup[];
};

export type MergedClaims = {
  claims: MaybeDrafted<FutureStateClaim>[];
  claimGroups: MaybeDrafted<FutureStateClaimGroup>[];
};

export type DraftEntities = {
  claims: FutureStateClaim;
  claimGroups: FutureStateClaimGroup;
};

export type DraftEntityKind = keyof DraftEntities;

export type AcceptFinishedPayload = {
  futureStateId: ArdoqId;
  acceptedIds: ArdoqId[];
  persistedIdByDraftId: Record<ArdoqId, ArdoqId>;
};

export const emptyDrafts = (futureStateId: ArdoqId): FutureStateDrafts => ({
  futureStateId,
  latestDraftVersion: 0,
  accepting: false,
  draftIdByAlias: {},
  claims: {},
  claimGroups: {},
});

export const draftsForFutureState = (
  state: FutureStateDraftClaimsState,
  futureStateId: ArdoqId
): FutureStateDrafts => state[futureStateId] ?? emptyDrafts(futureStateId);

export const hasDrafts = ({ claims, claimGroups }: FutureStateDrafts) =>
  Object.keys(claims).length > 0 || Object.keys(claimGroups).length > 0;

export const withNextDraftVersion = (
  drafts: FutureStateDrafts
): FutureStateDrafts => ({
  ...drafts,
  latestDraftVersion: drafts.latestDraftVersion + 1,
});

export const writeDraft = <Kind extends DraftEntityKind>(
  drafts: FutureStateDrafts,
  entityKind: Kind,
  entity: DraftEntities[Kind],
  draftKind: DraftKind
): FutureStateDrafts => ({
  ...drafts,
  [entityKind]: {
    ...drafts[entityKind],
    [entity._id]: {
      ...entity,
      draftKind,
      draftVersion: drafts.latestDraftVersion,
    },
  },
});

const draftIds = (drafts: FutureStateDrafts): ArdoqId[] => [
  ...Object.keys(drafts.claims),
  ...Object.keys(drafts.claimGroups),
];

const createdIds = (drafts: FutureStateDrafts): ArdoqId[] =>
  [...Object.values(drafts.claims), ...Object.values(drafts.claimGroups)]
    .filter(({ draftKind }) => draftKind === 'create')
    .map(({ _id }) => _id);

export const removeEntries = (
  drafts: FutureStateDrafts,
  ids: ArdoqId[]
): FutureStateDrafts => {
  const removedCreates = new Set(
    createdIds(drafts).filter(id => ids.includes(id))
  );
  return {
    ...drafts,
    claims: omit(drafts.claims, ids),
    claimGroups: omit(drafts.claimGroups, ids),
    draftIdByAlias: omitBy(drafts.draftIdByAlias, id => removedCreates.has(id)),
  };
};

export const clearDrafts = (drafts: FutureStateDrafts): FutureStateDrafts =>
  removeEntries(withNextDraftVersion(drafts), draftIds(drafts));

export const toDraftAliasOf = (
  draftIdByAlias: FutureStateDrafts['draftIdByAlias']
): ((id: ArdoqId) => DraftAlias) => {
  const aliasById = invert(draftIdByAlias);
  return id => aliasById[id] ?? id;
};

export type DraftOp = DraftKind | 'discard';

export type DraftRejection = 'noDraft' | 'deleteDraft';

type DraftMove =
  | { ok: true; kind: DraftKind | null }
  | { ok: false; reason: DraftRejection };

const moveTo = (kind: DraftKind | null): DraftMove => ({ ok: true, kind });

export const transition = (
  prev: DraftKind | undefined,
  op: DraftOp
): DraftMove => {
  if (op === 'discard') {
    return prev ? moveTo(null) : { ok: false, reason: 'noDraft' };
  }
  if (prev === 'create') return moveTo(op === 'delete' ? null : 'create');
  if (prev === 'delete' && op === 'update') {
    return { ok: false, reason: 'deleteDraft' };
  }
  return moveTo(op);
};

const createdClaimIdsInGroup = (
  drafts: FutureStateDrafts,
  groupId: ArdoqId
): ArdoqId[] =>
  Object.values(drafts.claims)
    .filter(
      claim => claim.draftKind === 'create' && claim.claimGroupId === groupId
    )
    .map(({ _id }) => _id);

const idsRemovedByDiscard = (
  drafts: FutureStateDrafts,
  entityKind: DraftEntityKind,
  id: ArdoqId
): ArdoqId[] =>
  entityKind === 'claimGroups' && drafts.claimGroups[id]?.draftKind === 'create'
    ? [id, ...createdClaimIdsInGroup(drafts, id)]
    : [id];

export const discardDraft = (
  drafts: FutureStateDrafts,
  entityKind: DraftEntityKind,
  id: ArdoqId
): FutureStateDrafts =>
  removeEntries(drafts, idsRemovedByDiscard(drafts, entityKind, id));

export const claimsWithMissingGroup = (
  drafts: FutureStateDrafts,
  persistedGroupIds: ReadonlySet<ArdoqId>
): Drafted<FutureStateClaim>[] =>
  Object.values(drafts.claims).filter(
    ({ claimGroupId }) =>
      claimGroupId &&
      !Object.hasOwn(drafts.claimGroups, claimGroupId) &&
      !persistedGroupIds.has(claimGroupId)
  );

const missingFromPersisted = (
  drafts: FutureStateDrafts,
  persisted: PersistedClaims
): Pick<Drafted<FutureStateClaim>, '_id' | 'draftKind'>[] => {
  const persistedIds = new Set(
    [...persisted.claims, ...persisted.claimGroups].map(({ _id }) => _id)
  );
  return [
    ...Object.values(drafts.claims),
    ...Object.values(drafts.claimGroups),
  ].filter(
    ({ draftKind, _id }) => draftKind !== 'create' && !persistedIds.has(_id)
  );
};

const idsOfKind = (
  entries: Pick<Drafted<FutureStateClaim>, '_id' | 'draftKind'>[],
  draftKind: DraftKind
): ArdoqId[] =>
  entries.filter(entry => entry.draftKind === draftKind).map(({ _id }) => _id);

export const orphanedDrafts = (
  drafts: FutureStateDrafts,
  persisted: PersistedClaims
): { dead: ArdoqId[]; lost: ArdoqId[] } => {
  const gone = missingFromPersisted(drafts, persisted);
  const dead = idsOfKind(gone, 'delete');
  const lostUpdates = idsOfKind(gone, 'update');
  const lostClaims = claimsWithMissingGroup(
    removeEntries(drafts, [...dead, ...lostUpdates]),
    new Set(persisted.claimGroups.map(({ _id }) => _id))
  )
    .filter(({ draftKind }) => draftKind !== 'delete')
    .map(({ _id }) => _id);
  return { dead, lost: [...lostUpdates, ...lostClaims] };
};

export const startAccept = (drafts: FutureStateDrafts): FutureStateDrafts => ({
  ...drafts,
  accepting: true,
});

export const REFERENCE_PROPERTIES: ReadonlySet<string> = new Set([
  'parent',
  'source',
  'target',
]);

export const remapClaimIds = <Claim extends FutureStateClaim>(
  claim: Claim,
  mapId: (id: ArdoqId) => ArdoqId
): Claim => ({
  ...claim,
  _id: mapId(claim._id),
  claimGroupId: claim.claimGroupId && mapId(claim.claimGroupId),
  targetId: claim.targetId && mapId(claim.targetId),
  assertProperties:
    claim.assertProperties &&
    mapValues(claim.assertProperties, (value, key) =>
      REFERENCE_PROPERTIES.has(key) && typeof value === 'string'
        ? mapId(value)
        : value
    ),
});

export const finishAccept = (
  drafts: FutureStateDrafts,
  { acceptedIds, persistedIdByDraftId }: AcceptFinishedPayload
): FutureStateDrafts => {
  const toPersistedId = (id: ArdoqId): ArdoqId =>
    persistedIdByDraftId[id] ?? id;
  return {
    ...withNextDraftVersion(drafts),
    accepting: false,
    draftIdByAlias: mapValues(drafts.draftIdByAlias, toPersistedId),
    claims: mapValues(omit(drafts.claims, acceptedIds), claim =>
      remapClaimIds(claim, toPersistedId)
    ),
    claimGroups: omit(drafts.claimGroups, acceptedIds),
  };
};

const overlay = <Entity extends { _id: ArdoqId; _version: number }>(
  persisted: Entity[],
  drafts: Record<ArdoqId, Drafted<Entity>>
): MaybeDrafted<Entity>[] => [
  ...persisted.map(entity => {
    const draft = drafts[entity._id];
    return draft ? { ...draft, _version: entity._version } : entity;
  }),
  ...Object.values(drafts).filter(({ draftKind }) => draftKind === 'create'),
];

export const mergeDrafts = ({
  persisted,
  drafts,
}: {
  persisted: PersistedClaims;
  drafts: FutureStateDrafts | null;
}): MergedClaims => {
  const claimGroups = overlay(persisted.claimGroups, drafts?.claimGroups ?? {});
  const deletedGroups = keyBy(
    claimGroups.filter(({ draftKind }) => draftKind === 'delete'),
    '_id'
  );
  const claims = overlay(persisted.claims, drafts?.claims ?? {}).flatMap(
    (claim): MaybeDrafted<FutureStateClaim>[] => {
      const group = claim.claimGroupId && deletedGroups[claim.claimGroupId];
      if (!group) return [claim];
      if (claim.draftKind === 'create') return [];
      return [
        { ...claim, draftKind: 'delete', draftVersion: group.draftVersion },
      ];
    }
  );
  return { claims, claimGroups };
};

export const draftClaimIdsOf = (
  claims: MaybeDrafted<FutureStateClaim>[]
): ReadonlySet<ArdoqId> =>
  new Set(
    claims
      .filter(({ draftKind }) => draftKind !== undefined)
      .map(({ _id }) => _id)
  );

export const hasDraftClaim = (
  claimIds: ArdoqId[],
  draftClaimIds: ReadonlySet<ArdoqId>
): boolean => claimIds.some(id => draftClaimIds.has(id));

const RENDER_KIND: Record<DraftKind, { send: boolean; exclude: boolean }> = {
  create: { send: true, exclude: false },
  update: { send: true, exclude: true },
  delete: { send: false, exclude: true },
};

export const toRenderClaim = (
  claim: FutureStateClaim
): FutureStateRenderClaim => {
  const base = {
    claimId: claim._id,
    claimType: claim.claimType,
    claimGroupId: claim.claimGroupId ?? undefined,
  };

  if (claim.targetId !== null && claim.targetId !== undefined) {
    return {
      ...base,
      entityType: claim.entityType,
      targetId: claim.targetId,
      assertProperties: claim.assertProperties ?? undefined,
    };
  }
  if (claim.entityType === 'component') {
    return {
      ...base,
      entityType: 'component',
      assertProperties: untargetedComponentClaimWithParentEnforced(claim),
    };
  }
  return {
    ...base,
    entityType: 'reference',
    assertProperties: claim.assertProperties ?? undefined,
  };
};

export const toRenderRequest = ({
  claims,
}: MergedClaims): Required<FutureStateRenderRequest> =>
  claims.reduce<Required<FutureStateRenderRequest>>(
    (request, claim) => {
      const { send, exclude } = claim.draftKind
        ? RENDER_KIND[claim.draftKind]
        : { send: false, exclude: false };
      return {
        claims: send
          ? [...request.claims, toRenderClaim(claim)]
          : request.claims,
        excludeClaimIds: exclude
          ? [...request.excludeClaimIds, claim._id]
          : request.excludeClaimIds,
      };
    },
    { claims: [], excludeClaimIds: [] }
  );
